import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Save,
  X,
  Loader,
  CheckCircle2,
  AlertCircle,
  BookOpen,
  ChevronDown,
  User,
  Check,
  RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useData } from '../../contexts/DataContext';
import resultApi from '../../api/services/resultApi';
import EXAM_API from '../../api/services/examApi';

const ManualResultEntry = ({ selectedExam, selectedClass, selectedSection, onSuccess }) => {
  const { students, currentAcademicYear } = useData();
  const [examDetails, setExamDetails] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [studentFilter, setStudentFilter] = useState('');
  const [batchData, setBatchData] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingExam, setLoadingExam] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [savingRow, setSavingRow] = useState(null);

  // Filter students based on current selection and search filter
  const filteredStudents = useMemo(() => {
    return students.filter(student => {
      const matchesSearch = 
        student.firstName?.toLowerCase().includes(studentFilter.toLowerCase()) ||
        student.lastName?.toLowerCase().includes(studentFilter.toLowerCase()) ||
        student.studentRegistrationId?.toLowerCase().includes(studentFilter.toLowerCase());
      
      return matchesSearch;
    });
  }, [students, studentFilter]);

  // Load exam details and subjects
  useEffect(() => {
    if (selectedExam && selectedClass) {
      loadExamDetails();
    }
  }, [selectedExam, selectedClass, selectedSection]);

  const loadExamDetails = async () => {
    setLoadingExam(true);
    try {
      const validation = await resultApi.validateExamAssignment(
        selectedExam,
        selectedClass,
        selectedSection || null
      );

      if (validation.success && validation.data?.subjects) {
        setExamDetails({
          examName: validation.data.examName,
          examType: validation.data.examType || 'Exam',
          subjects: validation.data.subjects,
          gradingSystem: validation.data.gradingSystem
        });
        setSubjects(validation.data.subjects);
      }
    } catch (error) {
      toast.error('Failed to load exam structure');
    } finally {
      setLoadingExam(false);
    }
  };

  // Load existing results when subject changes to avoid overwriting
  useEffect(() => {
    if (selectedSubject && students.length > 0) {
      loadExistingResults();
    } else {
      setBatchData({});
    }
  }, [selectedSubject, students]);

  const loadExistingResults = async () => {
    setLoading(true);
    try {
      const response = await resultApi.getAllResults({
        examId: selectedExam,
        unitId: selectedClass,
        subjectId: selectedSubject,
        ...(selectedSection && { sectionId: selectedSection })
      });

      const existingResults = response.data?.results || response.data || [];
      const initialBatch = {};

      students.forEach(student => {
        const result = existingResults.find(r => r.studentRegistrationId === student.studentRegistrationId);
        if (result) {
          initialBatch[student.studentRegistrationId] = {
            resultId: result._id,
            obtainedMarks: result.obtainedMarks,
            remarks: result.remarks || '',
            markComponents: (result.markComponents || result.markComponents)?.reduce((acc, c) => ({ ...acc, [c.name]: c.obtainedMarks }), {}) || {},
            isSaved: true,
            isDirty: false
          };
        } else {
          initialBatch[student.studentRegistrationId] = {
            obtainedMarks: '',
            remarks: '',
            markComponents: {},
            isSaved: false,
            isDirty: false
          };
        }
      });
      setBatchData(initialBatch);
    } catch (error) {
      console.error('Failed to load existing results', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedSubjectDetails = useMemo(() => 
    subjects.find(s => (s._id || s.subjectId || s.id)?.toString() === selectedSubject),
    [subjects, selectedSubject]
  );

  const handleMarkChange = (regId, value) => {
    setBatchData(prev => ({
      ...prev,
      [regId]: { ...prev[regId], obtainedMarks: value, isDirty: true }
    }));
  };

  const handleComponentMarkChange = (regId, compName, value) => {
    setBatchData(prev => {
      const row = prev[regId];
      const newComponents = { ...row.markComponents, [compName]: value };
      
      // Calculate total from components
      let total = 0;
      Object.values(newComponents).forEach(v => { total += parseFloat(v) || 0; });

      return {
        ...prev,
        [regId]: {
          ...prev[regId],
          markComponents: newComponents,
          obtainedMarks: total,
          isDirty: true
        }
      };
    });
  };

  const handleRemarksChange = (regId, value) => {
    setBatchData(prev => ({
      ...prev,
      [regId]: { ...prev[regId], remarks: value, isDirty: true }
    }));
  };

  const handleSaveRow = async (regId, silent = false) => {
    const data = batchData[regId];
    if (!data.isDirty) return;

    if (!silent) setSavingRow(regId);
    try {
      const payload = {
        studentRegistrationId: regId,
        unitId: selectedClass,
        ...(selectedSection && { sectionId: selectedSection }),
        subjectId: selectedSubject,
        subjectName: selectedSubjectDetails.subjectName,
        examType: examDetails.examType,
        examName: examDetails.examName,
        examId: selectedExam,
        academicYear: currentAcademicYear || '2023-24',
        totalMarks: selectedSubjectDetails.totalMarks,
        obtainedMarks: parseFloat(data.obtainedMarks) || 0,
        remarks: data.remarks,
        examDate: new Date().toISOString()
      };

      const subjectComponents = selectedSubjectDetails.markComponents || selectedSubjectDetails.markComponents || [];
      if (subjectComponents.length > 0) {
        payload.markComponents = subjectComponents.map(c => ({
          name: c.name,
          maxMarks: c.maxMarks,
          obtainedMarks: parseFloat(data.markComponents[c.name]) || 0
        }));
      }

      let res;
      if (data.resultId) {
        res = await resultApi.updateResult(data.resultId, payload);
      } else {
        res = await resultApi.createResult(payload);
      }

      if (res.success) {
        setBatchData(prev => ({
          ...prev,
          [regId]: { ...prev[regId], isSaved: true, isDirty: false, resultId: res.data?._id || data.resultId }
        }));
        if (!silent) toast.success('Saved');
      }
    } catch (error) {
      if (!silent) toast.error(error.response?.data?.message || error.message || 'Failed to save');
      throw error;
    } finally {
      if (!silent) setSavingRow(null);
    }
  };

  const handleSaveAll = async () => {
    const dirtyIds = Object.keys(batchData).filter(id => batchData[id].isDirty);
    if (dirtyIds.length === 0) {
      toast.error('No changes to save');
      return;
    }

    setSavingAll(true);
    let successCount = 0;
    for (const id of dirtyIds) {
      try {
        await handleSaveRow(id, true);
        successCount++;
      } catch (e) {
        console.error(`Failed to save ${id}`, e);
      }
    }
    setSavingAll(false);
    
    if (successCount > 0) {
      toast.success(`Successfully saved ${successCount} results`);
      if (onSuccess) onSuccess();
    }
  };

  if (loadingExam) {
    return (
      <div className="flex flex-col items-center justify-center p-12">
        <Loader className="h-8 w-8 animate-spin mb-4 text-gray-900" />
        <p className="text-sm font-medium text-gray-500">Loading exam structure...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Subject Selector and Header Stats */}
      <div className="bg-white border border-gray-200 rounded-md p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1 max-w-sm">
            <label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2 mb-2">
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-900 text-white text-xs font-bold">4</span>
              Select Subject
            </label>
            <div className="relative">
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="w-full bg-white border border-gray-300 rounded-md py-2.5 pl-4 pr-10 text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 outline-none appearance-none cursor-pointer"
              >
                <option value="">Choose Subject...</option>
                {subjects.map(sub => (
                  <option key={sub._id || sub.subjectId || sub.id} value={sub._id || sub.subjectId || sub.id}>
                    {sub.subjectName} (Max: {sub.totalMarks})
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {selectedSubject && (
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Students</p>
                <p className="text-lg font-bold text-gray-900">{students.length}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Unsaved</p>
                <p className="text-lg font-bold text-amber-600">
                  {Object.values(batchData).filter(d => d.isDirty).length}
                </p>
              </div>
              <button
                onClick={loadExistingResults}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                title="Refresh"
              >
                <RefreshCw size={18} className={`text-gray-500 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          )}
        </div>
      </div>

      {selectedSubject && (
        <div className="space-y-6">
          {/* Search Filter */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Filter by name or ID..."
              value={studentFilter}
              onChange={(e) => setStudentFilter(e.target.value)}
              className="w-full bg-white border border-gray-300 rounded-md py-2.5 pl-10 pr-4 text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 outline-none"
            />
          </div>

          {/* Result Entry Table */}
          <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 font-semibold text-gray-700">Student</th>
                    <th className="px-4 py-3 font-semibold text-gray-700 w-48">Marks</th>
                    <th className="px-4 py-3 font-semibold text-gray-700">Remarks</th>
                    <th className="px-4 py-3 font-semibold text-gray-700 w-20">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredStudents.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                        No students found matching your filter
                      </td>
                    </tr>
                  ) : (
                    filteredStudents.map(student => {
                      const data = batchData[student.studentRegistrationId] || { obtainedMarks: '', remarks: '', markComponents: {} };
                      const subjectComponents = selectedSubjectDetails?.markComponents || selectedSubjectDetails?.markComponents || [];
                      const showComponentInputs = subjectComponents.length >= 2;

                      return (
                        <tr key={student.studentRegistrationId} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded bg-gray-100 flex items-center justify-center text-gray-600 font-semibold text-xs">
                                {student.firstName[0]}{student.lastName[0]}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{student.firstName} {student.lastName}</p>
                                <p className="text-xs text-gray-500">ID: {student.studentRegistrationId}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {showComponentInputs ? (
                              <div className="space-y-2">
                                {subjectComponents.map(comp => (
                                  <div key={comp.name} className="flex items-center gap-2">
                                    <span className="text-[10px] text-gray-500 w-12 truncate" title={comp.name}>{comp.name}</span>
                                    <input
                                      type="number"
                                      className="w-16 h-8 bg-white border border-gray-300 rounded px-2 text-xs focus:border-gray-900 outline-none"
                                      value={data.markComponents[comp.name] || ''}
                                      onChange={(e) => handleComponentMarkChange(student.studentRegistrationId, comp.name, e.target.value)}
                                      onWheel={(e) => e.target.blur()}
                                      placeholder={`/ ${comp.maxMarks}`}
                                    />
                                  </div>
                                ))}
                                <div className="text-[10px] font-bold text-gray-900 pt-1 border-t border-gray-100">
                                  Total: {data.obtainedMarks} / {selectedSubjectDetails.totalMarks}
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  className="w-24 h-9 bg-white border border-gray-300 rounded px-3 text-sm focus:border-gray-900 outline-none"
                                  value={data.obtainedMarks}
                                  onChange={(e) => handleMarkChange(student.studentRegistrationId, e.target.value)}
                                  onWheel={(e) => e.target.blur()}
                                  placeholder={`/ ${selectedSubjectDetails?.totalMarks}`}
                                />
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              className="w-full h-9 bg-white border border-gray-300 rounded px-3 text-sm focus:border-gray-900 outline-none"
                              value={data.remarks}
                              onChange={(e) => handleRemarksChange(student.studentRegistrationId, e.target.value)}
                              placeholder="Add entry remarks..."
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-center">
                              {savingRow === student.studentRegistrationId ? (
                                <Loader className="w-4 h-4 animate-spin text-gray-400" />
                              ) : data.isSaved && !data.isDirty ? (
                                <CheckCircle2 className="w-5 h-5 text-green-500" />
                              ) : data.isDirty ? (
                                <button
                                  onClick={() => handleSaveRow(student.studentRegistrationId)}
                                  className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-900 transition-colors"
                                >
                                  <Save size={18} />
                                </button>
                              ) : (
                                <div className="w-5 h-5" />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Table Footer Actions */}
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                {Object.values(batchData).filter(d => d.isDirty).length} rows have unsaved changes
              </p>
              <button
                onClick={handleSaveAll}
                disabled={savingAll || Object.values(batchData).filter(d => d.isDirty).length === 0}
                className="bg-gray-900 text-white rounded-md py-2 px-6 font-semibold text-xs hover:bg-gray-800 transition-all flex items-center gap-2 disabled:opacity-50"
              >
                {savingAll ? <Loader className="w-3 h-3 animate-spin" /> : <Save size={14} />}
                {savingAll ? 'Saving Results...' : 'Save All Results'}
              </button>
            </div>
          </div>
        </div>
      )}

      {!selectedSubject && (
        <div className="bg-gray-50 border border-gray-200 border-dashed rounded-md p-12 text-center">
          <BookOpen className="mx-auto h-8 w-8 text-gray-400 mb-3" />
          <h3 className="text-sm font-semibold text-gray-900">Step 4: Select a Subject</h3>
          <p className="text-xs text-gray-500 mt-1">Please select a subject from the dropdown above to start entering marks</p>
        </div>
      )}
    </div>
  );
};

export default ManualResultEntry;
